import { useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useMy } from '@/hooks/useMy';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { RiskSeverity } from '@/types';

export interface RiskCountsResult {
  [RiskSeverity.Critical]: number;
  [RiskSeverity.High]: number;
  [RiskSeverity.Medium]: number;
  [RiskSeverity.Low]: number;
  [RiskSeverity.Info]: number;
  total: number;
}

export function useCustomerRiskCounts() {
  // Open (Demonstrated) risks
  const openQuery = useMy(
    {
      resource: 'statistic',
      query: 'my#status:O#now',
      desc: true,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  // Invalidate when any statistic is updated via WebSocket.
  // Statistics are written to the account partition, so use the
  // account-scoped connection (user: false). Debounce to avoid a cascade
  // of refetches when many stats update in quick succession.
  const debouncedInvalidate = useDebouncedCallback(() => {
    openQuery.invalidate();
  }, 2000);

  useRealtimeSubscription({
    patterns: [{ pattern: '#statistic#', matchType: 'prefix' }],
    user: false,
    onUpdate: debouncedInvalidate,
  });

  const dataMap = useMemo(() => {
    const map = new Map<string, number>();

    for (const item of openQuery.data ?? []) {
      const key = `${item.username}#${item.value}`;
      map.set(key, (map.get(key) ?? 0) + (item?.data?.count || 0));
    }

    return map;
  }, [openQuery.data]);

  return {
    ...openQuery,
    dataMap,
  };
}
